/**
 * Stella Maris Digital Catalogue — frontend behavior.
 * Fetches catalogue metadata from /api/apps, renders cards grouped by
 * category, and provides client-side search/filter across all groups.
 * Each card links directly to the live application; no external
 * functionality is embedded or proxied.
 */

const ICONS = {
  home: '<path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9"/>',
  utensils:
    '<path d="M7 3v7a2 2 0 0 0 2 2v9"/><path d="M7 3v7"/><path d="M11 3v7"/><path d="M17 3c-1.5 0-2.5 1.5-2.5 4s1 4 2.5 4v10"/>',
  cart: '<circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2.5 3h2l2.6 12.4a2 2 0 0 0 2 1.6h8.3a2 2 0 0 0 2-1.6L21 7H6.2"/>',
  store:
    '<path d="M3 9.5 4.5 4h15L21 9.5"/><path d="M3 9.5a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0"/><path d="M5 9.5V20h14V9.5"/><path d="M10 20v-6h4v6"/>',
  calendar:
    '<rect x="3.5" y="5" width="17" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3.5 10h17"/>',
  file: '<path d="M6 2.5h8l5 5V20a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4a1.5 1.5 0 0 1 1-1.5z"/><path d="M14 2.5V8h5"/><path d="M8.5 13h7"/><path d="M8.5 16.5h7"/>',
  activity: '<path d="M2 12h4l2-7 4 14 2-7h4l2 3h2"/>',
  shield:
    '<path d="M12 2 3 6v6c0 5 4 9 9 10 5-1 9-5 9-10V6l-9-4z"/><path d="M9 12l2 2 4-4"/>',
  briefcase:
    '<rect x="2.5" y="7" width="19" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M2.5 13h19"/>',
  "life-buoy":
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5"/><path d="M5.8 5.8l3.2 3.2"/><path d="M15 15l3.2 3.2"/><path d="M18.2 5.8 15 9"/><path d="M9 15l-3.2 3.2"/>',
  "bar-chart":
    '<path d="M4 20V10"/><path d="M12 20V4"/><path d="M20 20v-7"/><path d="M2 20h20"/>',
};

const EXTERNAL_ICON = '<path d="M7 17 17 7"/><path d="M8 7h9v9"/>';

const CATEGORY_ORDER = ["Informasi", "Layanan", "Administrasi"];

const groupsEl = document.getElementById("app-groups");
const searchEl = document.getElementById("app-search");
const searchHelpEl = document.getElementById("search-help");
const emptyStateEl = document.getElementById("empty-state");
const footerLinksEl = document.getElementById("footer-links");
const nav = document.getElementById("siteNav");
const menuBtn = document.getElementById("menuBtn");
const navLinks = document.getElementById("navLinks");
const scrollProgressEl = document.getElementById("scrollProgress");

let allApps = [];

function iconMarkup(key) {
  const path = ICONS[key] || ICONS.file;
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${path}</svg>`;
}

function safeHostname(url) {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return "";
  }
}

function escapeHtml(str) {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}

function slugify(str) {
  return str.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-");
}

function subLinksMarkup(app) {
  if (!Array.isArray(app.links) || !app.links.length) return "";
  return `
    <ul class="card-sub-links">
      ${app.links
        .map(
          (link) => `
        <li>
          <a
            href="${escapeHtml(link.url)}"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Buka ${escapeHtml(link.name)} (dibuka di tab baru)"
          >
            ${escapeHtml(link.name)}
            <svg class="ext-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${EXTERNAL_ICON}</svg>
          </a>
        </li>`
        )
        .join("")}
    </ul>
  `;
}

function cardMarkup(app, index) {
  const domain = safeHostname(app.url);
  const bannerStyle = app.image
    ? ` style="background-image:linear-gradient(135deg, rgba(0,51,102,.55), rgba(0,77,153,.45)), url('/static/img/${escapeHtml(app.image)}')"`
    : "";
  return `
    <article class="app-card program-card" style="animation-delay:${Math.min(index * 40, 400)}ms">
      <div class="program-icon-banner"${bannerStyle}>${iconMarkup(app.icon)}</div>
      <div class="program-content">
        <span class="card-category">${escapeHtml(app.category)}</span>
        <h3>${escapeHtml(app.name)}</h3>
        <p>${escapeHtml(app.description)}</p>
        <a
          class="btn-see-more"
          href="${escapeHtml(app.url)}"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Buka ${escapeHtml(app.name)} (dibuka di tab baru)${domain ? `, ${escapeHtml(domain)}` : ""}"
        >
          Buka Situs
          <svg class="ext-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${EXTERNAL_ICON}</svg>
        </a>
        ${subLinksMarkup(app)}
      </div>
    </article>
  `;
}

function groupByCategory(apps) {
  const groups = new Map();
  apps.forEach((app) => {
    if (!groups.has(app.category)) groups.set(app.category, []);
    groups.get(app.category).push(app);
  });

  const orderedKeys = [
    ...CATEGORY_ORDER.filter((c) => groups.has(c)),
    ...[...groups.keys()].filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return orderedKeys.map((category) => ({ category, apps: groups.get(category) }));
}

function groupMarkup(group, startIndex) {
  return `
    <div class="category-group" id="category-${slugify(group.category)}">
      <h3 class="category-group-title">${escapeHtml(group.category)}</h3>
      <div class="app-grid">
        ${group.apps.map((app, i) => cardMarkup(app, startIndex + i)).join("")}
      </div>
    </div>
  `;
}

function renderApps(apps) {
  if (!apps.length) {
    groupsEl.innerHTML = "";
    emptyStateEl.hidden = false;
    return;
  }
  emptyStateEl.hidden = true;
  let cardIndex = 0;
  groupsEl.innerHTML = groupByCategory(apps)
    .map((group) => {
      const markup = groupMarkup(group, cardIndex);
      cardIndex += group.apps.length;
      return markup;
    })
    .join("");
}

function renderFooterLinks(apps) {
  footerLinksEl.innerHTML = apps
    .map(
      (app) => `
      <li>
        <a href="${escapeHtml(app.url)}" target="_blank" rel="noopener noreferrer">
          ${escapeHtml(app.name)}
        </a>
      </li>`
    )
    .join("");
}

function applyFilter(query) {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? allApps.filter(
        (app) => app.name.toLowerCase().includes(q) || app.description.toLowerCase().includes(q)
      )
    : allApps;

  renderApps(filtered);

  searchHelpEl.textContent = q
    ? `${filtered.length} aplikasi cocok dengan "${query.trim()}"`
    : "";
}

function closeMobileMenu() {
  navLinks.classList.remove("open");
  menuBtn.classList.remove("open");
  menuBtn.setAttribute("aria-expanded", "false");
}

function initNavBehavior() {
  const onScroll = () => {
    nav.classList.toggle("scrolled", window.scrollY > 40);

    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - doc.clientHeight;
    const progress = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
    scrollProgressEl.style.width = `${progress}%`;
  };
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });

  menuBtn.addEventListener("click", () => {
    const isOpen = navLinks.classList.toggle("open");
    menuBtn.classList.toggle("open", isOpen);
    menuBtn.setAttribute("aria-expanded", String(isOpen));
  });

  navLinks.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", closeMobileMenu);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && navLinks.classList.contains("open")) {
      closeMobileMenu();
      menuBtn.focus();
    }
  });

  document.addEventListener("click", (e) => {
    if (
      navLinks.classList.contains("open") &&
      !navLinks.contains(e.target) &&
      !menuBtn.contains(e.target)
    ) {
      closeMobileMenu();
    }
  });
}

async function init() {
  initNavBehavior();

  try {
    const res = await fetch("/api/apps");
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    allApps = await res.json();
    groupsEl.setAttribute("aria-busy", "false");
    renderApps(allApps);
    renderFooterLinks(allApps);
  } catch (err) {
    groupsEl.setAttribute("aria-busy", "false");
    groupsEl.innerHTML = `<p class="empty-state">Tidak dapat memuat katalog saat ini. Silakan muat ulang halaman.</p>`;
    console.error("Failed to load catalogue", err);
    return;
  }

  searchEl.addEventListener("input", (e) => applyFilter(e.target.value));
}

init();
